/**
 * Mapping exercice → fichier animation Mixamo (.glb)
 *
 * Pour ajouter une animation :
 * 1. Aller sur mixamo.com, chercher l'animation (ex: "squat")
 * 2. Telecharger en format FBX (ou glTF si dispo)
 * 3. Convertir en .glb avec https://gltf.report/ ou Blender
 * 4. Placer dans /public/models/anims/
 * 5. Ajouter l'entree ici
 *
 * Noms Mixamo recommandes par exercice :
 * - Squat:           "Standing Idle To Squat" ou "Squat"
 * - Squat saute:     "Jumping Jacks" ou "Jump Squat"
 * - Pompes:          "Push Up"
 * - Burpee:          "Burpee" (chercher dans la lib)
 * - Jumping Jacks:   "Jumping Jacks"
 * - Mountain Climber: "Mountain Climbers"
 * - Planche:         "Plank" ou "Push Up Hold"
 * - Fentes:          "Lunges"
 * - High Knees:      "Running In Place"
 * - Box Jump:        "Box Jump"
 */

// Animation file mapping: exercise keyword → animation .glb path
const ANIM_MAP = {
  // Squats
  squat:            '/models/anims/squat.glb',
  squat_saute:      '/models/anims/squat_jump.glb',
  squat_jump:       '/models/anims/squat_jump.glb',
  pistol_squat:     '/models/anims/pistol_squat.glb',

  // Upper body
  pompes:           '/models/anims/pushup.glb',
  pompes_genoux:    '/models/anims/pushup.glb',
  pompes_claquement:'/models/anims/pushup.glb',
  pompes_classiques:'/models/anims/pushup.glb',

  // Plank variants
  planche:          '/models/anims/plank.glb',
  planche_statique: '/models/anims/plank.glb',
  planche_dynamique:'/models/anims/plank.glb',
  planche_tap:      '/models/anims/plank.glb',

  // Cardio
  jumping_jacks:    '/models/anims/jumping_jacks.glb',
  jumping_jacks_lents: '/models/anims/jumping_jacks.glb',
  high_knees:       '/models/anims/high_knees.glb',
  montees_genoux:   '/models/anims/high_knees.glb',
  sprints_place:    '/models/anims/high_knees.glb',

  // Full body
  burpee:           '/models/anims/burpee.glb',
  burpees:          '/models/anims/burpee.glb',
  burpees_complets: '/models/anims/burpee.glb',
  burpees_sans_saut:'/models/anims/burpee.glb',

  // Lunges
  fentes:           '/models/anims/lunge.glb',
  fentes_sautees:   '/models/anims/lunge.glb',

  // Mountain climbers
  mountain_climbers:'/models/anims/mountain_climbers.glb',
  mountain_climbers_lents: '/models/anims/mountain_climbers.glb',
  mountain_climbers_rapides: '/models/anims/mountain_climbers.glb',

  // Other
  box_jump:         '/models/anims/box_jump.glb',
  box_jumps:        '/models/anims/box_jump.glb',
  marche:           '/models/anims/walk.glb',
  marche_sur_place: '/models/anims/walk.glb',
  step_touches:     '/models/anims/step_touch.glb',
  rotation_epaules: '/models/anims/arm_circles.glb',
  bear_crawl:       '/models/anims/bear_crawl.glb',

  // Advanced
  thruster:         '/models/anims/thruster.glb',
  thrusters:        '/models/anims/thruster.glb',
  muscle_ups:       '/models/anims/pullup.glb',
  pullup:           '/models/anims/pullup.glb',

  // Fallback — idle animation
  _default:         '/models/anims/idle.glb',
};

/**
 * Get animation URL for a given exercise ID or name
 * @param {string} exerciseId — e.g. "squat_poids_du_corps_0"
 * @returns {string} — path to .glb animation file
 */
export function getAnimationUrl(exerciseId) {
  if (!exerciseId) return ANIM_MAP._default;

  // Normalize: lowercase, strip trailing numbers/underscores
  const normalized = exerciseId
    .toLowerCase()
    .replace(/_\d+$/, '')     // Remove trailing _0, _1
    .replace(/^ai_/, '')      // Remove AI prefix
    .trim();

  // Direct match
  if (ANIM_MAP[normalized]) return ANIM_MAP[normalized];

  // Partial match — check if any key is contained in the exercise ID
  for (const [key, url] of Object.entries(ANIM_MAP)) {
    if (key === '_default') continue;
    if (normalized.includes(key) || key.includes(normalized)) {
      return url;
    }
  }

  return ANIM_MAP._default;
}

/**
 * Check if a .glb animation file exists at the given path
 * Uses a HEAD request to avoid downloading the full file
 */
export async function checkAnimationExists(url) {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

// Default avatar model path (rigged version with skeleton)
export const DEFAULT_AVATAR_URL = '/models/avatar_rigged.glb';
